import React, { useState } from 'react';
import { Coins, Plus, Loader2, Check, Sparkles, Zap, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { useWorkspace } from '@/context/WorkspaceContext';

// Use current origin for API calls
const API = `${window.location.origin}/api`;

const PACKAGES = [
  { id: 'starter', name: 'Starter', credits: 100, price: 499, icon: Sparkles, popular: false },
  { id: 'growth', name: 'Growth', credits: 300, price: 999, icon: Zap, popular: true },
  { id: 'pro', name: 'Pro', credits: 1000, price: 2499, icon: Crown, popular: false }
];

const CreditsDisplay = ({ showTopUp = true, compact = false }) => {
  const { credits, isOwner, refreshCredits } = useWorkspace();
  const [showModal, setShowModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleTopUp = async (pkg) => {
    setSelectedPackage(pkg.id);
    setProcessing(true);

    try {
      // Create order
      const orderResponse = await axios.post(
        `${API}/payments/create-order`,
        { package: pkg.id },
        { withCredentials: true }
      );

      const { order_id, amount, key_id } = orderResponse.data;

      // Load Razorpay script if not loaded
      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);
        await new Promise(resolve => script.onload = resolve);
      }

      // Open Razorpay checkout
      const options = {
        key: key_id,
        amount: amount,
        currency: 'INR',
        name: 'Customer Design Studio',
        description: `${pkg.name} Package - ${pkg.credits} Credits`,
        order_id: order_id,
        handler: async function (response) {
          try {
            // Verify payment
            await axios.post(
              `${API}/payments/verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              },
              { withCredentials: true }
            );

            toast.success(`${pkg.credits} credits added to your account!`);
            await refreshCredits();
            setShowModal(false);
          } catch (err) {
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        prefill: {},
        theme: {
          color: '#4F46E5'
        },
        modal: {
          ondismiss: function() {
            setProcessing(false);
            setSelectedPackage(null);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create order');
    } finally {
      setProcessing(false);
      setSelectedPackage(null);
    }
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 px-3 py-1.5 rounded-full border border-amber-500/20">
          <Coins className="w-4 h-4 text-amber-500" />
          <span className="font-semibold text-amber-600">{credits}</span>
        </div>
        {showTopUp && isOwner && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowModal(true)}
            className="h-8"
          >
            <Plus className="w-3 h-3 mr-1" />
            Top Up
          </Button>
        )}

        {/* Top-up Modal */}
        {showModal && (
          <TopUpModal
            onClose={() => setShowModal(false)}
            onSelectPackage={handleTopUp}
            selectedPackage={selectedPackage}
            processing={processing}
          />
        )}
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-100">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-zinc-900">Credits Balance</h3>
        <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
          <Coins className="w-5 h-5 text-amber-500" />
          <span className="text-2xl font-bold text-zinc-900">{credits}</span>
        </div>
      </div>

      {showTopUp && isOwner && (
        <Button
          onClick={() => setShowModal(true)}
          className="w-full bg-indigo-600 hover:bg-indigo-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Buy More Credits
        </Button>
      )}

      {/* Top-up Modal */}
      {showModal && (
        <TopUpModal
          onClose={() => setShowModal(false)}
          onSelectPackage={handleTopUp}
          selectedPackage={selectedPackage}
          processing={processing}
        />
      )}
    </div>
  );
};

const TopUpModal = ({ onClose, onSelectPackage, selectedPackage, processing }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-zinc-900 mb-2">Buy Credits</h2>
          <p className="text-zinc-600">Choose a package that works for you</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {PACKAGES.map((pkg) => {
            const Icon = pkg.icon;
            const isSelected = selectedPackage === pkg.id;
            
            return (
              <div
                key={pkg.id}
                className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all ${
                  pkg.popular 
                    ? 'border-indigo-500 bg-indigo-50' 
                    : 'border-zinc-200 hover:border-indigo-300'
                }`}
                onClick={() => !processing && onSelectPackage(pkg)}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-500 text-white text-xs px-3 py-1 rounded-full">
                    Most Popular
                  </div>
                )}
                
                <div className="text-center">
                  <div className={`w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center ${
                    pkg.popular ? 'bg-indigo-500' : 'bg-zinc-100'
                  }`}>
                    <Icon className={`w-6 h-6 ${pkg.popular ? 'text-white' : 'text-zinc-600'}`} />
                  </div>
                  
                  <h3 className="font-bold text-lg text-zinc-900 mb-1">{pkg.name}</h3>
                  <p className="text-3xl font-bold text-zinc-900 mb-1">
                    ₹{pkg.price}
                  </p>
                  <p className="text-sm text-zinc-500 mb-4">{pkg.credits} credits</p>
                  <p className="text-xs text-zinc-400">
                    ₹{(pkg.price / pkg.credits).toFixed(2)}/credit
                  </p>
                </div>

                {isSelected && processing && (
                  <div className="absolute inset-0 bg-white/80 rounded-xl flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex justify-end">
          <Button variant="ghost" onClick={onClose} disabled={processing}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreditsDisplay;
